import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Card,
  CardMedia,
  CardContent,
  IconButton,
  Chip,
  Grid,
} from '@mui/material';
import { ChevronLeft, ChevronRight, Wifi, Tv, Coffee, Pool } from '@mui/icons-material';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import SearchRooms from './components/SearchRooms';
import RoomList from './components/RoomList';
import BookingDrawer from './components/BookingDrawer';
import BookingHistory from './components/BookingHistory';
import { useStore } from './store/useStore';

const CustomArrow = ({ direction, onClick }: { direction: 'left' | 'right', onClick?: () => void }) => (
  <IconButton
    onClick={onClick}
    sx={{
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      zIndex: 2,
      bgcolor: 'rgba(255, 255, 255, 0.8)',
      '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' },
      ...(direction === 'left' ? { left: 8 } : { right: 8 }),
    }}
  >
    {direction === 'left' ? <ChevronLeft /> : <ChevronRight />}
  </IconButton>
);

const Dashboard = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedRooms, setSelectedRooms] = useState<{
    categoryId: string;
    quantity: number;
  }[]>([]);
  const [searchParams, setSearchParams] = useState<{
    checkIn: Date | null;
    checkOut: Date | null;
    guests: number;
    isSearched: boolean;
  }>({
    checkIn: null,
    checkOut: null,
    guests: 1,
    isSearched: false,
  });
  
  const { fetchRoomCategories, fetchBookings, roomCategories } = useStore();
//   const { fetchRoomCategories, fetchBookings, roomCategories }: any = useState({});

  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  useEffect(() => {
    fetchRoomCategories();
    fetchBookings();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const handleSearch = (params: { checkIn: Date | null; checkOut: Date | null; guests: number }) => {
    setSearchParams({ ...params, isSearched: true });
    setSelectedRooms([]);
  };

  const getAmenityIcon = (amenity: string) => {
    if (amenity.toLowerCase().includes('wifi')) return <Wifi fontSize="small" />;
    if (amenity.toLowerCase().includes('tv')) return <Tv fontSize="small" />;
    if (amenity.toLowerCase().includes('breakfast')) return <Coffee fontSize="small" />;
    if (amenity.toLowerCase().includes('pool')) return <Pool fontSize="small" />;
    return null;
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    prevArrow: <CustomArrow direction="left" />,
    nextArrow: <CustomArrow direction="right" />,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 1,
        }
      }
    ]
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Room Reservation Section */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Room Booking
        </Typography>
        <SearchRooms onSearch={handleSearch} />
        {searchParams.isSearched && (
          <>
            <RoomList
              selectedRooms={selectedRooms}
              onRoomSelection={setSelectedRooms}
            />
            {selectedRooms.length > 0 && (
              <Box sx={{ mt: 3, position: 'sticky', bottom: 0, textAlign: 'right', bgcolor: '#fff', padding: '5px 0' }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => setIsDrawerOpen(true)}
                >
                  Proceed with Booking
                </Button>
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Available Room Categories Section */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Room Categories
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Explore our luxurious room categories and choose the perfect accommodation for your stay
        </Typography>
        
        <Box sx={{ mx: -2 }}>
          <Slider {...sliderSettings}>
            {roomCategories?.length && roomCategories.map((category: any) => (
              <Box key={category.id} sx={{ px: 2 }}>
                <Card sx={{ height: '100%' }}>
                  <CardMedia
                    component="img"
                    height="200"
                    image={category.imageUrl}
                    alt={category.name}
                  />
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      {category.name}
                    </Typography>
                    <Typography variant="subtitle1" color="primary" gutterBottom>
                      ₹{category.pricePerNight} per night
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {category.description}
                    </Typography>
                    
                    <Grid>
                      {category.amenities
                        .filter((amenity: any) => getAmenityIcon(amenity))
                        .slice(0, 4)
                        .map((amenity: any) => (
                          <Chip
                            key={amenity}
                            // icon={getAmenityIcon(amenity)}
                            label={amenity.split(' ')[0]}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                    </Grid>
                    
                    <Typography variant="caption" color="text.secondary" display="block">
                      Max Guests: {category.maxGuests}
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            ))}
          </Slider>
        </Box>
      </Paper>

      <BookingHistory />
      <BookingDrawer
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        selectedRooms={selectedRooms}
        searchParams={searchParams}
      />
    </Container>
  );
};

export default Dashboard;